import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { IconDevices, IconLock } from "@tabler/icons-react";
import { PageTitle, ConfirmDialog, Pagination, CircleLoading } from "components";
import useDeviceList from "hooks/remote/useDeviceList";
import { DeviceTab, DEVICE_TAB_ACTIVE, DEVICE_TAB_BLOCKED } from "const/deviceManagement.const";
import { FilterBar, DeviceTable, BulkActionBar } from "./components";
import { ColumnConfig } from "./components/ColumnVisibilityDropdown";
import DeviceDetail from "./DeviceDetail";
import useDeviceActions from "./hooks/useDeviceActions";
import { useTranslation } from "react-i18next";

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: "deviceName", label: "Tên thiết bị", visible: true, required: true },
  { key: "accountName", label: "Tên tài khoản", visible: true },
  { key: "phone", label: "Số điện thoại", visible: true },
  { key: "role", label: "Vai trò", visible: false },
  { key: "type", label: "Loại thiết bị", visible: false },
  { key: "os", label: "Hệ điều hành", visible: true },
  { key: "status", label: "Trạng thái", visible: true },
  { key: "firstLoginDate", label: "Đăng nhập đầu", visible: true },
  { key: "version", label: "Phiên bản", visible: true },
  { key: "lastActivity", label: "Hoạt động cuối", visible: true },
  { key: "actions", label: "Thao tác", visible: true, required: true },
];

const LOCAL_STORAGE_KEY = "device_mgmt_column_visibility";

const DeviceManagement: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation("deviceManagement");
  const searchParams = new URLSearchParams(location.search);
  const detailDeviceId = searchParams.get("detail");

  const [activeTab, setActiveTab] = useState<DeviceTab>(DEVICE_TAB_ACTIVE);

  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed: Record<string, boolean> = JSON.parse(saved);
        return DEFAULT_COLUMNS.map((col) => ({
          ...col,
          visible: parsed[col.key] !== undefined ? parsed[col.key] : col.visible,
        }));
      }
    } catch {
      // ignore
    }
    return DEFAULT_COLUMNS;
  });

  const handleColumnToggle = useCallback((key: string, visible: boolean) => {
    setColumns((prev) => {
      const updated = prev.map((col) => (col.key === key ? { ...col, visible } : col));
      try {
        const saveObj: Record<string, boolean> = {};
        updated.forEach((c) => {
          saveObj[c.key] = c.visible;
        });
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(saveObj));
      } catch {
        // ignore
      }
      return updated;
    });
  }, []);

  const handleResetColumns = useCallback(() => {
    setColumns(DEFAULT_COLUMNS);
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const columnVisibilityMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    columns.forEach((col) => {
      map[col.key] = col.visible;
    });
    return map;
  }, [columns]);

  const {
    loading,
    searchText,
    selectedTypes,
    selectedOS,
    selectedVersions,
    filteredOSOptions,
    displayGroups,
    page,
    totalPages,
    isSelectAllMode,
    excludeDeviceIds,
    totalDevices,
    deviceTypeParam,
    refetch,
    handleSearchChange,
    handleTypesChange,
    handleOSChange,
    handleVersionsChange,
    handleResetAll,
    handlePageChange,
    handleToggleSelectAll,
    handleExcludeDevice,
    setIsSelectAllMode,
    setExcludeDeviceIds,
    selectedDeviceIds,
    setSelectedDeviceIds,
    versionOptions,
  } = useDeviceList({ activeTab });

  const {
    confirmDialog,
    openBlockDialog,
    openUnblockDialog,
    openLogoutDialog,
    closeDialog,
    confirmAction,
    getDialogTitle,
  } = useDeviceActions({
    onSuccess: () => {
      setSelectedDeviceIds([]);
      setIsSelectAllMode(false);
      setExcludeDeviceIds([]);
      refetch();
    },
  });

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const handleToggleGroup = useCallback((accountId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  }, []);

  const handleCheckboxChange = (deviceId: string, isChecked: boolean) => {
    if (isSelectAllMode) {
      handleExcludeDevice(deviceId);
    } else {
      setSelectedDeviceIds((prev: string[]) => {
        if (isChecked) {
          return [...prev, deviceId];
        }
        return prev.filter((id: string) => id !== deviceId);
      });
    }
  };

  const allPageDeviceIds = useMemo(() => {
    const ids: string[] = [];
    displayGroups.forEach((group) => {
      group.devices.forEach((device) => ids.push(device.id));
    });
    return ids;
  }, [displayGroups]);

  const isAllSelected =
    allPageDeviceIds.length > 0 &&
    allPageDeviceIds.every((id) => selectedDeviceIds.includes(id));

  const isHeaderIndeterminate =
    selectedDeviceIds.length > 0 && !isAllSelected;

  const handleSelectAll = (isChecked: boolean) => {
    if (isSelectAllMode) {
      if (isChecked) {
        setExcludeDeviceIds((prev: string[]) =>
          prev.filter((id) => !allPageDeviceIds.includes(id))
        );
      } else {
        setExcludeDeviceIds((prev: string[]) => {
          const set = new Set(prev);
          allPageDeviceIds.forEach((id) => set.add(id));
          return Array.from(set);
        });
      }
    } else {
      if (isChecked) {
        setSelectedDeviceIds((prev: string[]) => {
          const set = new Set(prev);
          allPageDeviceIds.forEach((id) => set.add(id));
          return Array.from(set);
        });
      } else {
        setSelectedDeviceIds((prev: string[]) =>
          prev.filter((id) => !allPageDeviceIds.includes(id))
        );
      }
    }
  };

  // Auto switch to normal mode when selected count drops to 0
  useEffect(() => {
    if (isSelectAllMode && totalDevices > 0 && excludeDeviceIds.length >= totalDevices) {
      setIsSelectAllMode(false);
      setExcludeDeviceIds([]);
    }
  }, [isSelectAllMode, totalDevices, excludeDeviceIds]);

  const selectedCount = isSelectAllMode
    ? totalDevices - excludeDeviceIds.length
    : selectedDeviceIds.length;

  const handleConfirm = () => {
    confirmAction({
      isSelectAllMode,
      excludeDeviceIds,
      selectedDeviceIds,
      searchText,
      deviceTypeParam,
      selectedVersions,
    });
  };

  if (detailDeviceId) {
    return <DeviceDetail onRefreshList={refetch} />;
  }

  return (
    <div className="d-flex flex-column" style={{ height: "100%" }}>
      <PageTitle>{t("TXT_DEVICE_MANAGEMENT_TITLE")}</PageTitle>

      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === DEVICE_TAB_ACTIVE ? "active" : ""}`}
                onClick={() => setActiveTab(DEVICE_TAB_ACTIVE)}
              >
                <IconDevices size={16} className="me-1" />
                {t("TXT_DEVICE_TAB_ACTIVE_LIST")}
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === DEVICE_TAB_BLOCKED ? "active" : ""}`}
                onClick={() => setActiveTab(DEVICE_TAB_BLOCKED)}
              >
                <IconLock size={16} className="me-1" />
                {t("TXT_DEVICE_TAB_BLOCKED_LIST")}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body d-flex flex-column gap-3">
          <FilterBar
            searchText={searchText}
            onSearchChange={handleSearchChange}
            selectedTypes={selectedTypes}
            onTypesChange={handleTypesChange}
            selectedOS={selectedOS}
            onOSChange={handleOSChange}
            filteredOSOptions={filteredOSOptions}
            selectedVersions={selectedVersions}
            onVersionsChange={handleVersionsChange}
            versionOptions={versionOptions}
            onResetAll={handleResetAll}
            columns={columns}
            onColumnToggle={handleColumnToggle}
            onResetColumns={handleResetColumns}
          />

          <BulkActionBar
            selectedCount={selectedCount}
            totalDevices={totalDevices}
            onLogout={() => openLogoutDialog()}
            onBlock={() => openBlockDialog()}
            onUnblock={() => openUnblockDialog()}
            isSelectAllMode={isSelectAllMode}
            onToggleSelectAll={handleToggleSelectAll}
            activeTab={activeTab}
          />

          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <CircleLoading />
            </div>
          ) : (
            <DeviceTable
              groupedDevices={displayGroups}
              selectedDeviceIds={selectedDeviceIds}
              onCheckboxChange={handleCheckboxChange}
              onLogoutDevice={(deviceId) => openLogoutDialog(deviceId)}
              expandedGroups={expandedGroups}
              onToggleGroup={handleToggleGroup}
              isAllSelected={isAllSelected}
              isIndeterminate={isHeaderIndeterminate}
              onSelectAll={handleSelectAll}
              isSelectAllMode={isSelectAllMode}
              excludeDeviceIds={excludeDeviceIds}
              columnVisibility={columnVisibilityMap}
            />
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={getDialogTitle(selectedCount)}
        onDeny={closeDialog}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default DeviceManagement;